import IconCard, { CardDescription, CardValue } from "components/company/cards/IconCard";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import HouseIcon from "components/svg/HouseIcon";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface IHeadquartersCardProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment | IAmericanCompanyIssuerTopLevelFragment;
}

export default function HeadquartersCard({ issuer }: IHeadquartersCardProps) {
  const intl = useIntl();
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isAmericanIssuer = issuer.__typename === "AmericanCompanyIssuer";

  if (isBrazilianIssuer) {
    if (issuer.headquarters.country === "Brasil") {
      let stateName = issuer.headquarters.state;
      const stateKey = issuer.headquarters.state as keyof typeof STATE_NAMES;
      if (stateName.length === 2 && STATE_NAMES[stateKey]) {
        stateName = STATE_NAMES[stateKey];
      }
      return (
        <IconCard primaryColor={colors.blue[600]} secondaryColor={colors.blue[50]} icon={<HouseIcon />}>
          <CardValue title={issuer.headquarters.city}>{issuer.headquarters.city}</CardValue>
          <CardDescription>
            <FormattedMessage
              defaultMessage="Sede • {stateName}"
              id="wtUu/I"
              values={{ stateName }}
              description="Headquarters card"
            />
          </CardDescription>
        </IconCard>
      );
    }

    return (
      <IconCard primaryColor={colors.blue[600]} secondaryColor={colors.blue[50]} icon={<HouseIcon />}>
        <CardValue title={issuer.headquarters.country}>{issuer.headquarters.country}</CardValue>
        <CardDescription>
          <FormattedMessage defaultMessage="Sede" id="k+A8uv" description="Headquarters" />
        </CardDescription>
      </IconCard>
    );
  }

  const standardizedCountries = genStandardizedCountries(intl);

  if (isAmericanIssuer && issuer.countryCode) {
    const country = standardizedCountries.find((candidate) => {
      return candidate.isoCountryCode === issuer.countryCode;
    });
    if (!country) {
      return null;
    }
    return (
      <IconCard
        primaryColor={colors.blue[600]}
        secondaryColor={colors.blue[50]}
        icon={
          <img
            title={country.name}
            className="h-7 w-7 squircle"
            alt={`Flag of ${country.name}`}
            // TODO: Refatorar para 1 componente separado
            src={`https://s.fndm.to/svg/flags/oval/${country.isoCountryCode}.svg`}
            width={28}
            height={28}
          />
        }
      >
        <CardValue title={country.name}>{country.name}</CardValue>
        <CardDescription>
          <FormattedMessage defaultMessage="País" id="M/hE4A" />
        </CardDescription>
      </IconCard>
    );
  }

  return null;
}

const STATE_NAMES = {
  AC: "Acre",
  AL: "Alagoas",
  AM: "Amazonas",
  AP: "Amapá",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MG: "Minas Gerais",
  MS: "Mato Grosso do Sul",
  MT: "Mato Grosso",
  PA: "Pará",
  PB: "Paraíba",
  PE: "Pernambuco",
  PI: "Piauí",
  PR: "Paraná",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RO: "Rondônia",
  RR: "Roraima",
  RS: "Rio Grande do Sul",
  SC: "Santa Catarina",
  SE: "Sergipe",
  SP: "São Paulo",
  TO: "Tocantins",
};
